import { Fragment } from "react";
import { Box, Table, Text } from "@chakra-ui/react";
import { useSelector } from "react-redux";

import ServicesSessionTimeTable from "../tables/ServicesSessionTimeTable";
import { formatTime } from "../../../store/helpers";
import {
  getAgentReviewPriorityRows,
  getAgentServiceTimeInsights,
} from "../../../store/agentServiceTimeInsights";
import {
  AGENT_SERVICE_TIME_COLUMN_DEFINITIONS,
  normalizeRenderConfig,
  sortReportRows,
  usesUnifiedTable,
} from "../../../store/renderConfig";
import {
  resolveAgentServiceBranchTicketCount,
  resolveAgentServiceTicketCount,
} from "../../../store/agentServiceTimeMetrics";
import * as defaultSelectors from "../../../store/selectors/defaultSelector";
import ConfiguredReportTable from "../../reports/ConfiguredReportTable";
import {
  ReportSectionIntro,
  ReportTitleWithInfo,
} from "../../reports/ReportTitleWithInfo";

const AGENT_SERVICE_COLUMN = Object.freeze({
  id: "agentService",
  label: "Agent / Service",
  pinned: true,
});
const UNIFIED_DETAIL_COLUMNS = AGENT_SERVICE_TIME_COLUMN_DEFINITIONS.filter(
  (column) => column.id !== "agentName"
);

const formatPercent = (value) => `${Number(value || 0).toFixed(2)}%`;

const FLAG_LABELS = Object.freeze({
  "Slow average": "Slow service average",
  "Slow branch average": "Slow branch average",
  "High time / low tickets": "High time / low tickets",
  "Low volume": "Low volume",
  "High workload share": "High workload share",
});

const formatFlagReason = (agent = {}) => {
  const flags = Array.isArray(agent.flags) ? agent.flags : [];
  if (flags.length) {
    return flags.map((flag) => FLAG_LABELS[flag] || flag).join(", ");
  }
  if (agent.effectivenessBand === "Needs review") return "Slower than benchmark";
  return agent.effectivenessBand || "Expected range";
};

const getServiceRows = (branch, service) =>
  (service.agentsData || [])
    .filter((agent) => agent?.sum)
    .map((agent) => ({
      ...agent,
      branchName: branch.branchName,
      serviceName: service.serviceName || agent.serviceName || "",
    }));

const getRowsByService = (branch, renderConfig) =>
  (branch.servicesData || []).map((service) => ({
    serviceName: service.serviceName || "",
    rows: sortReportRows(getServiceRows(branch, service), renderConfig),
  }));

const renderMetricCell = (agentData, columnId) => {
  if (columnId === "ticketCount") return resolveAgentServiceTicketCount(agentData);
  if (columnId === "totalServiceTime") {
    return <Text color={"var(--green)"}>{formatTime(agentData.sum)}</Text>;
  }
  if (columnId === "percentage" || columnId === "serviceTimeShare") {
    return formatPercent(agentData.serviceTimeShare ?? agentData.totalPct ?? 0);
  }
  if (columnId === "average") return formatTime(agentData.avgPerTicket ?? agentData.average);
  if (columnId === "serviceAverage") return formatTime(agentData.serviceAverage);
  if (columnId === "avgVsServiceAveragePct") {
    return `${Number(agentData.avgVsServiceAveragePct || 0).toFixed(2)}%`;
  }
  if (columnId === "median") return formatTime(agentData.medianServiceTime ?? agentData.median);
  if (columnId === "p90") return formatTime(agentData.p90ServiceTime ?? agentData.p90);
  if (columnId === "activeDays") return agentData.activeDays ?? 0;
  if (columnId === "ticketsPerActiveDay") {
    return Number(agentData.ticketsPerActiveDay || 0).toFixed(2);
  }
  if (columnId === "flags") {
    return Array.isArray(agentData.flags) && agentData.flags.length
      ? agentData.flags.join(", ")
      : agentData.effectivenessBand || "";
  }
  if (columnId === "min") return formatTime(agentData.min);
  if (columnId === "max") return formatTime(agentData.max);
  return null;
};

const EnterpriseSummary = ({ branch }) => {
  const { branchSummary, agentSummary, serviceSummary, exceptionSummary } =
    getAgentServiceTimeInsights(branch);
  const reviewPriorityAgents = getAgentReviewPriorityRows(agentSummary);
  const serviceRisks = [...serviceSummary]
    .sort(
      (left, right) =>
        (right.reviewAgentCount || 0) - (left.reviewAgentCount || 0) ||
        (right.noWorkAgentCount || 0) - (left.noWorkAgentCount || 0)
    )
    .slice(0, 5);

  return (
    <Box className="agentServiceEnterpriseSummary" mb="20px">
      <ReportTitleWithInfo description="Summary is based on completed tickets only. Agents are flagged when their handling time is materially slower than the service or branch benchmark, or when their time share is high compared with their ticket share.">
        Agent Effectiveness Summary
      </ReportTitleWithInfo>
      <Box className="agentServiceKpiGrid">
        <Box><Text>Completed Tickets</Text><strong>{branchSummary.completedTickets}</strong></Box>
        <Box><Text>Total Service Time</Text><strong>{formatTime(branchSummary.totalServiceTime)}</strong></Box>
        <Box><Text>Avg / Ticket</Text><strong>{formatTime(branchSummary.averageServiceTime)}</strong></Box>
        <Box><Text>Active Agents</Text><strong>{branchSummary.activeAgentCount}</strong></Box>
        <Box><Text>Flagged Agents</Text><strong>{branchSummary.reviewAgentCount}</strong></Box>
        <Box><Text>No Completed Tickets</Text><strong>{branchSummary.noWorkAgentCount}</strong></Box>
      </Box>

      <Box className="agentServiceSummaryGrid">
        <Box>
          <ReportTitleWithInfo as="h4" description="Prioritized agents are ranked by flagged status, number of flags, variance from branch average, workload balance, and ticket volume. Review Reason shows the actual trigger.">
            Agent Flag Priority
          </ReportTitleWithInfo>
          <Table.Root className="stripedTable reportDataTable reportDataTable--compact" size="sm">
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeader>#</Table.ColumnHeader>
                <Table.ColumnHeader>Agent</Table.ColumnHeader>
                <Table.ColumnHeader>Tickets</Table.ColumnHeader>
                <Table.ColumnHeader>Avg / Ticket</Table.ColumnHeader>
                <Table.ColumnHeader>Vs Branch Avg</Table.ColumnHeader>
                <Table.ColumnHeader>Time Share</Table.ColumnHeader>
                <Table.ColumnHeader>Review Reason</Table.ColumnHeader>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {reviewPriorityAgents.map((agent, index) => (
                <Table.Row key={`${agent.agentName}-${index}`}>
                  <Table.Cell>{index + 1}</Table.Cell>
                  <Table.Cell>{agent.agentName}</Table.Cell>
                  <Table.Cell>{agent.completedTickets}</Table.Cell>
                  <Table.Cell>{formatTime(agent.averageServiceTime)}</Table.Cell>
                  <Table.Cell>{formatPercent(agent.avgVsBranchAveragePct)}</Table.Cell>
                  <Table.Cell>{formatPercent(agent.serviceTimeShare)}</Table.Cell>
                  <Table.Cell>{formatFlagReason(agent)}</Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table.Root>
        </Box>

        <Box>
          <ReportTitleWithInfo as="h4" description="Shows services with the highest concentration of flagged agents. No Completed Tickets counts agents in that service group who completed zero tickets in the period.">
            Service Flag Summary
          </ReportTitleWithInfo>
          <Table.Root className="stripedTable reportDataTable reportDataTable--compact" size="sm">
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeader>#</Table.ColumnHeader>
                <Table.ColumnHeader>Service</Table.ColumnHeader>
                <Table.ColumnHeader>Tickets</Table.ColumnHeader>
                <Table.ColumnHeader>Active Agents</Table.ColumnHeader>
                <Table.ColumnHeader>Flagged Agents</Table.ColumnHeader>
                <Table.ColumnHeader>No Completed Tickets</Table.ColumnHeader>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {serviceRisks.map((service, index) => (
                <Table.Row key={`${service.serviceName}-${index}`}>
                  <Table.Cell>{index + 1}</Table.Cell>
                  <Table.Cell>{service.serviceName}</Table.Cell>
                  <Table.Cell>{service.completedTickets}</Table.Cell>
                  <Table.Cell>{service.activeAgentCount}</Table.Cell>
                  <Table.Cell>{service.reviewAgentCount}</Table.Cell>
                  <Table.Cell>{service.noWorkAgentCount}</Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table.Root>
        </Box>
      </Box>

      <Text fontSize="sm" color="gray.600" mt="10px">
        Exceptions: {exceptionSummary.reviewAgents.length} flagged agents, {exceptionSummary.noWorkAgents.length} agents have no completed tickets{branchSummary.unassignedCompletedTickets > 0 ? `, ${branchSummary.unassignedCompletedTickets} tickets were unassigned` : ""}.
      </Text>
    </Box>
  );
};

const AgentServiceCell = ({ agentData }) => (
  <Box>
    <Text>{agentData.agentName}</Text>
    <Text className="agentServiceMutedCell">{agentData.serviceName}</Text>
  </Box>
);

const UnifiedAgentServiceTable = ({ branch, renderConfig }) => {
  const config = normalizeRenderConfig(renderConfig);
  const rows = getRowsByService(branch, config).flatMap((service) => service.rows);

  return (
    <ConfiguredReportTable
      pinnedColumns={[AGENT_SERVICE_COLUMN]}
      columns={UNIFIED_DETAIL_COLUMNS}
      rows={rows}
      renderConfig={config}
      renderCell={(agentData, columnId) => {
        if (columnId === "agentService") return <AgentServiceCell agentData={agentData} />;
        return renderMetricCell(agentData, columnId);
      }}
      presortedRows={true}
    />
  );
};

const AgentServicesSessionTime = ({ reportIndex, data }) => {
  const renderConfig = useSelector(defaultSelectors.selectRenderConfig);
  const useUnifiedTable = usesUnifiedTable(renderConfig);

  // Safety check to ensure data is an array
  if (!Array.isArray(data)) {
    return <Box><Text>Invalid data format</Text></Box>;
  }

  return (
    <Box>
      {data.map(
        (branch, branchIndex) => {
          const { branchName, servicesData = [], numServices } = branch;
          const title = `${reportIndex || ""}.${
            branchIndex + 1
          }. ${branchName}`;
          const completedTicketCount = resolveAgentServiceBranchTicketCount(branch);
          return (
            <Fragment key={`${reportIndex}-${branchIndex}`}>
              <h2>{title}</h2>
              <Text>
                {numServices} services
                {completedTicketCount !== undefined ? ` · ${completedTicketCount} completed tickets` : ""}.
              </Text>
              <Text fontSize="sm" color="gray.600" mb="12px">
                Based on completed tickets only. Share of Service Time is each agent total service time divided by total service time for the service.
              </Text>
              <EnterpriseSummary branch={branch} />
              <ReportSectionIntro
                title="Detailed Agent Service Time"
                description="Full detail by agent and service. This is the operational table for comparing ticket counts, total service time, average handling time, and configured columns after the summary has identified the main flags."
              />
              {useUnifiedTable ? (
                <Box className="reportTableContainer" mt="16px" mb="24px">
                  <UnifiedAgentServiceTable
                    branch={{ branchName, servicesData }}
                    renderConfig={renderConfig}
                  />
                </Box>
              ) : (
                Array.isArray(servicesData) && servicesData.map((data, index) => {
                return (
                  <ServicesSessionTimeTable
                    key={index}
                    index={index}
                    serviceData={data}
                    branchIndex={branchIndex}
                    reportIndex={reportIndex}
                    branchName={branchName}
                  />
                );
              }))}
            </Fragment>
          );
        }
      )}
    </Box>
  );
};

export default AgentServicesSessionTime;
